/**
 * Webtech HR Engine™ - SuperAdmin Cockpit Dashboard
 * Version simplifiée avec sidebar navigation
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import SuperAdminBillingDashboard from '../components/SuperAdminBillingDashboard';
import SuperAdminUsersDashboard from '../components/SuperAdminUsersDashboard';
import SuperAdminAIDashboard from '../components/SuperAdminAIDashboard';
import SuperAdminModulesDashboard from '../components/SuperAdminModulesDashboard';
import SuperAdminSettingsDashboard from '../components/SuperAdminSettingsDashboard';
import SuperAdminAdminsDashboard from '../components/SuperAdminAdminsDashboard';
import SuperAdminExpirationsDashboard from '../components/SuperAdminExpirationsDashboard';
import SuperAdminBackupDashboard from '../components/SuperAdminBackupDashboard';
import SuperAdminLeanInsights from '../components/SuperAdminLeanInsights';
import SuperAdminGrowthInsights from '../components/SuperAdminGrowthInsights';
import AICreditsManager from '../components/AICreditsManager';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Building2,
  Users,
  CreditCard,
  TrendingUp,
  Rocket,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  DollarSign,
  Activity,
  Brain,
  RefreshCw,
  Search,
  Filter,
  MoreVertical,
  Eye,
  Pause,
  Play,
  Crown,
  Gift,
  Shield,
  ChevronRight,
  Loader2,
  Bell,
  Settings,
  Layers,
  LayoutDashboard,
  ExternalLink,
  Trash2,
  Clock,
  Calendar,
  AlertCircle,
  Mail,
  Database,
  Sparkles
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

// Formatage monétaire F CFA
const formatCurrency = (amount) => {
  if (!amount) return '0 F CFA';
  return new Intl.NumberFormat('fr-FR').format(amount) + ' F CFA';
};

// Formatage date
const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

// Navigation items pour la sidebar
const navItems = [
  { id: 'overview', label: 'Vue d\'ensemble', icon: LayoutDashboard },
  { id: 'lean-insights', label: 'Insights Lean', icon: TrendingUp },
  { id: 'growth', label: 'Croissance', icon: Rocket },
  { id: 'companies', label: 'Entreprises', icon: Building2 },
  { id: 'expirations', label: 'Expirations', icon: Clock },
  { id: 'billing', label: 'Facturation', icon: CreditCard },
  { id: 'users', label: 'Utilisateurs', icon: Users },
  { id: 'ai-credits', label: 'Crédits IA', icon: Sparkles },
  { id: 'ai', label: 'Supervision IA', icon: Brain },
  { id: 'modules', label: 'Modules', icon: Layers },
  { id: 'backup', label: 'Sauvegarde', icon: Database },
  { id: 'settings', label: 'Paramètres', icon: Settings },
  { id: 'admins', label: 'Administrateurs', icon: Shield },
];

// Company Status Badge
function StatusBadge({ status }) {
  const config = {
    active: { label: 'Actif', class: 'bg-emerald-100 text-emerald-700' },
    at_risk: { label: 'À risque', class: 'bg-amber-100 text-amber-700' },
    suspended: { label: 'Suspendu', class: 'bg-red-100 text-red-700' },
    free: { label: 'STARTER', class: 'bg-slate-100 text-slate-700' },
    demo: { label: 'Démo', class: 'bg-purple-100 text-purple-700' },
    trial: { label: 'Essai BUSINESS', class: 'bg-purple-100 text-purple-700' },
    grace_period: { label: 'Période de grâce', class: 'bg-orange-100 text-orange-700' },
    expired: { label: 'Démo expirée', class: 'bg-red-100 text-red-700' },
  };
  const c = config[status] || config.free;
  return <Badge className={c.class}>{c.label}</Badge>;
}

// Company Action Dialog
function CompanyActionDialog({ isOpen, onClose, company, onAction }) {
  const [action, setAction] = useState('');
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState(14);
  const [durationValue, setDurationValue] = useState(1);
  const [durationUnit, setDurationUnit] = useState('month');
  const [userCount, setUserCount] = useState(10);
  const [maxUsers, setMaxUsers] = useState(10);
  const [maxEmployees, setMaxEmployees] = useState(500);
  const [useCustomLimits, setUseCustomLimits] = useState(false);
  const [demoPlan, setDemoPlan] = useState('pro');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!action) return;
    setLoading(true);
    try {
      const upgradeOptions = { 
        durationValue, 
        durationUnit, 
        userCount,
        maxUsers: useCustomLimits ? maxUsers : userCount,
        maxEmployees: useCustomLimits ? maxEmployees : userCount * 50,
        demoPlan,
      };
      await onAction(company.id, action, reason, duration, upgradeOptions);
      resetForm();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setAction('');
    setReason('');
    setDuration(14);
    setDurationValue(1);
    setDurationUnit('month');
    setUserCount(10);
    setMaxUsers(10);
    setMaxEmployees(500);
    setUseCustomLimits(false);
    setDemoPlan('pro');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Mettre à jour les limites par défaut quand userCount change
  const handleUserCountChange = (value) => {
    const count = parseInt(value);
    setUserCount(count);
    if (!useCustomLimits) {
      setMaxUsers(count);
      setMaxEmployees(count * 50);
    }
  };

  if (!company) return null;

  const isPro = company.subscription?.plan === 'pro';
  const isSuspended = company.subscription?.status === 'suspended';
  const isDemo = !!company.subscription?.isDemo;
  const demoExpiresAt = company.subscription?.demoExpiresAt;
  const planLabel = isPro ? 'BUSINESS' : 'STARTER';
  const statusLabel = company.subscription?.status === 'trial' ? 'Essai BUSINESS' : 
                      company.subscription?.status === 'grace_period' ? 'Période de grâce' :
                      isSuspended ? 'Suspendu' : 'Actif';

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="company-action-dialog">
        <DialogHeader>
          <DialogTitle>Gérer {company.name}</DialogTitle>
          <DialogDescription>
            Plan: <span className={isPro ? 'text-amber-600 font-medium' : ''}>{planLabel}</span> • 
            Statut: <span className={isSuspended ? 'text-red-600' : 'text-green-600'}>{statusLabel}</span> •
            {company.employeesCount || 0} employés
            {isDemo && (
              <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-100 text-purple-700">
                Démo{demoExpiresAt ? ` · expire le ${new Date(demoExpiresAt).toLocaleDateString('fr-FR')}` : ''}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Action</Label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger data-testid="action-select-trigger">
                <SelectValue placeholder="Choisir une action" />
              </SelectTrigger>
              <SelectContent>
                {/* Actions de statut */}
                {!isSuspended && (
                  <SelectItem value="suspend" data-testid="action-suspend">🚫 Suspendre le compte</SelectItem>
                )}
                {isSuspended && (
                  <SelectItem value="reactivate" data-testid="action-reactivate">✅ Réactiver le compte</SelectItem>
                )}
                
                {/* Actions de plan */}
                {!isPro && (
                  <>
                    <SelectItem value="upgrade" data-testid="action-upgrade">⬆️ Passer en BUSINESS</SelectItem>
                    <SelectItem value="grant_trial" data-testid="action-grant-trial">🎁 Accorder un essai BUSINESS</SelectItem>
                  </>
                )}
                {isPro && (
                  <SelectItem value="downgrade" data-testid="action-downgrade">⬇️ Rétrograder en STARTER</SelectItem>
                )}
                
                {/* Actions de démo */}
                {!isDemo && (
                  <SelectItem value="activate_demo" data-testid="action-activate-demo">🧪 Activer le mode démo</SelectItem>
                )}
                {isDemo && (
                  <>
                    <SelectItem value="extend_demo" data-testid="action-extend-demo">⏱️ Étendre la démo</SelectItem>
                    <SelectItem value="end_demo" data-testid="action-end-demo">🛑 Terminer la démo</SelectItem>
                  </>
                )}
                
                {/* Autres actions */}
                <SelectItem value="grant_grace_period" data-testid="action-grace-period">⏳ Accorder un délai de grâce</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Durée pour les actions qui en ont besoin */}
          {(action === 'grant_trial' || action === 'grant_grace_period' || action === 'activate_demo' || action === 'extend_demo') && (
            <div className="space-y-2">
              <Label>
                {action === 'extend_demo' ? 'Prolongation (jours)' : 'Durée (jours)'}
              </Label>
              <Select value={duration.toString()} onValueChange={(v) => setDuration(parseInt(v))}>
                <SelectTrigger data-testid="duration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 jours</SelectItem>
                  <SelectItem value="14">14 jours</SelectItem>
                  <SelectItem value="30">30 jours</SelectItem>
                  <SelectItem value="60">60 jours</SelectItem>
                  <SelectItem value="90">90 jours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Choix du plan accordé pendant la démo */}
          {action === 'activate_demo' && (
            <div className="space-y-2">
              <Label>Plan accordé pendant la démo</Label>
              <Select value={demoPlan} onValueChange={setDemoPlan}>
                <SelectTrigger data-testid="demo-plan-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pro">BUSINESS (toutes fonctionnalités)</SelectItem>
                  <SelectItem value="free">STARTER (fonctionnalités de base)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Configuration pour upgrade BUSINESS */}
          {action === 'upgrade' && (
            <div className="space-y-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-900 flex items-center gap-2">
                <Crown className="w-4 h-4" />
                Configuration du plan BUSINESS
              </h4>
              
              {/* Nombre d'utilisateurs */}
              <div className="space-y-2">
                <Label>Nombre d'utilisateurs (facturation)</Label>
                <Select value={userCount.toString()} onValueChange={handleUserCountChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 utilisateurs</SelectItem>
                    <SelectItem value="10">10 utilisateurs</SelectItem>
                    <SelectItem value="20">20 utilisateurs</SelectItem>
                    <SelectItem value="50">50 utilisateurs</SelectItem>
                    <SelectItem value="100">100 utilisateurs</SelectItem>
                    <SelectItem value="200">200 utilisateurs</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Limites personnalisées */}
              <div className="space-y-3 pt-2 border-t">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="customLimits"
                    checked={useCustomLimits}
                    onChange={(e) => setUseCustomLimits(e.target.checked)}
                    className="rounded border-slate-300"
                  />
                  <Label htmlFor="customLimits" className="cursor-pointer text-sm">
                    Définir des limites personnalisées
                  </Label>
                </div>
                
                {useCustomLimits && (
                  <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="space-y-1">
                      <Label className="text-xs">Max utilisateurs</Label>
                      <Input
                        type="number"
                        min="1"
                        value={maxUsers}
                        onChange={(e) => setMaxUsers(parseInt(e.target.value) || 1)}
                        className="h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Max employés</Label>
                      <Input
                        type="number"
                        min="1"
                        value={maxEmployees}
                        onChange={(e) => setMaxEmployees(parseInt(e.target.value) || 1)}
                        className="h-9"
                      />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Période de validité */}
              <div className="space-y-2">
                <Label>Période de validité</Label>
                <div className="flex gap-2">
                  <Select value={durationValue.toString()} onValueChange={(v) => setDurationValue(parseInt(v))}>
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 6, 9, 12, 15, 18, 24].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={durationUnit} onValueChange={setDurationUnit}>
                    <SelectTrigger className="flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="day">Jour(s)</SelectItem>
                      <SelectItem value="month">Mois</SelectItem>
                      <SelectItem value="year">Année(s)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Aperçu */}
              <div className="text-sm text-blue-700 bg-blue-100 p-3 rounded space-y-1">
                <p>📅 Le plan expirera dans <strong>{durationValue} {durationUnit === 'day' ? 'jour(s)' : durationUnit === 'month' ? 'mois' : 'an(s)'}</strong></p>
                <p>👥 Limite: <strong>{useCustomLimits ? maxUsers : userCount}</strong> utilisateurs, <strong>{useCustomLimits ? maxEmployees : userCount * 50}</strong> employés</p>
                <p className="text-xs text-blue-600">⚠️ Notifications envoyées à 30j, 15j et 1j avant expiration</p>
              </div>
            </div>
          )}
          
          <div className="space-y-2">
            <Label>Raison / Note (optionnel)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ajouter une note..."
              rows={2}
            />
          </div>
          
          {/* Avertissement pour suspension */}
          {action === 'suspend' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              ⚠️ Les utilisateurs de cette entreprise ne pourront plus se connecter.
            </div>
          )}
          
          {/* Info pour essai BUSINESS */}
          {action === 'grant_trial' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
              🎁 L'entreprise aura accès à toutes les fonctionnalités BUSINESS pendant {duration} jours.
            </div>
          )}

          {/* Info pour activation démo */}
          {action === 'activate_demo' && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-700">
              🧪 L'entreprise sera marquée comme <strong>Démo</strong> pendant {duration} jours.
              {demoPlan === 'pro'
                ? ' Accès à toutes les fonctionnalités BUSINESS.'
                : ' Accès aux fonctionnalités STARTER uniquement.'}
            </div>
          )}

          {/* Info pour prolongation démo */}
          {action === 'extend_demo' && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-700">
              ⏱️ La démo sera prolongée de <strong>{duration} jours</strong>
              {demoExpiresAt && (
                <> à partir de l'expiration actuelle ({new Date(demoExpiresAt).toLocaleDateString('fr-FR')}).</>
              )}
            </div>
          )}

          {/* Avertissement pour fin de démo */}
          {action === 'end_demo' && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-700">
              🛑 Le mode démo sera désactivé. Le compte conservera son plan actuel ({planLabel}).
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Annuler</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!action || loading}
            className={action === 'suspend' ? 'bg-red-600 hover:bg-red-700' : ''}
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Confirmer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Delete Company Confirmation Dialog
function DeleteCompanyDialog({ isOpen, onClose, company, onDelete }) {
  const [confirmName, setConfirmName] = useState('');
  const [loading, setLoading] = useState(false);

  const canDelete = confirmName.trim().toLowerCase() === (company?.name || '').toLowerCase();

  const handleDelete = async () => {
    if (!canDelete) return;
    setLoading(true);
    try {
      await onDelete(company.id);
      setConfirmName('');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  // Reset on close
  const handleClose = () => {
    setConfirmName('');
    onClose();
  };

  if (!company) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="w-5 h-5" />
            Supprimer l'entreprise
          </DialogTitle>
          <DialogDescription>
            Cette action est <strong>irréversible</strong>. Toutes les données associées seront définitivement supprimées.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Warning Box */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-semibold text-red-800 mb-2">Données qui seront supprimées :</h4>
            <ul className="text-sm text-red-700 space-y-1">
              <li>• L'entreprise <strong>{company.name}</strong></li>
              <li>• Tous les utilisateurs ({company.userCount || 0})</li>
              <li>• Tous les employés ({company.employeesCount || company.employeeCount || 0})</li>
              <li>• Départements, sites, abonnements</li>
              <li>• Évaluations, objectifs, compétences</li>
              <li>• Factures, paiements, analyses IA</li>
            </ul>
          </div>

          {/* Confirmation Input */}
          <div className="space-y-2">
            <Label>
              Pour confirmer, tapez <strong className="text-red-600">{company.name}</strong>
            </Label>
            <Input
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={`Tapez "${company.name}" pour confirmer`}
              className="border-red-200 focus:border-red-400"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Annuler
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleDelete} 
            disabled={!canDelete || loading}
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Supprimer définitivement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Company Limits Dialog
function CompanyLimitsDialog({ isOpen, onClose, company, onSuccess }) {
  const [maxUsers, setMaxUsers] = useState(10);
  const [maxEmployees, setMaxEmployees] = useState(500);
  const [currentLimits, setCurrentLimits] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (isOpen && company) {
      fetchCurrentLimits();
    }
  }, [isOpen, company]);

  const fetchCurrentLimits = async () => {
    setFetching(true);
    try {
      const response = await api.get(`/superadmin/companies/${company.id}/limits`);
      setCurrentLimits(response.data);
      setMaxUsers(response.data.limits.maxUsers || 10);
      setMaxEmployees(response.data.limits.maxEmployees || 500);
    } catch (error) {
      toast.error('Erreur lors du chargement des limites');
    } finally {
      setFetching(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await api.put(`/superadmin/companies/${company.id}/limits`, {
        max_users: maxUsers,
        max_employees: maxEmployees
      });
      toast.success(response.data.message);
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  if (!company) return null;

  const isPro = company.subscription?.plan === 'pro';
  const usersUsage = currentLimits ? Math.round((currentLimits.current.users / currentLimits.limits.maxUsers) * 100) : 0;
  const employeesUsage = currentLimits ? Math.round((currentLimits.current.employees / currentLimits.limits.maxEmployees) * 100) : 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Modifier les limites
          </DialogTitle>
          <DialogDescription>
            {company.name} • 
            <span className={isPro ? 'text-amber-600 font-medium ml-1' : 'ml-1'}>{isPro ? 'PRO' : 'Gratuit'}</span>
          </DialogDescription>
        </DialogHeader>

        {fetching ? (
          <div className="py-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
            <p className="text-sm text-slate-500 mt-2">Chargement...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Usage actuel */}
            {currentLimits && (
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <h4 className="text-sm font-medium text-slate-700">Utilisation actuelle</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span>Utilisateurs</span>
                      <span className="font-medium">{currentLimits.current.users} / {currentLimits.limits.maxUsers}</span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${usersUsage > 90 ? 'bg-red-500' : usersUsage > 70 ? 'bg-amber-500' : 'bg-green-500'}`}
                        style={{ width: `${Math.min(usersUsage, 100)}%` }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span>Employés</span>
                      <span className="font-medium">{currentLimits.current.employees} / {currentLimits.limits.maxEmployees}</span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${employeesUsage > 90 ? 'bg-red-500' : employeesUsage > 70 ? 'bg-amber-500' : 'bg-green-500'}`}
                        style={{ width: `${Math.min(employeesUsage, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Nouvelles limites */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-slate-700">Nouvelles limites</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Max utilisateurs</Label>
                  <Input
                    type="number"
                    min="1"
                    value={maxUsers}
                    onChange={(e) => setMaxUsers(parseInt(e.target.value) || 1)}
                    className="h-10"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Max employés</Label>
                  <Input
                    type="number"
                    min="1"
                    value={maxEmployees}
                    onChange={(e) => setMaxEmployees(parseInt(e.target.value) || 1)}
                    className="h-10"
                  />
                </div>
              </div>
            </div>

            {/* Avertissement si les nouvelles limites sont inférieures à l'utilisation actuelle */}
            {currentLimits && (
              (maxUsers < currentLimits.current.users || maxEmployees < currentLimits.current.employees) && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                  ⚠️ <strong>Attention :</strong> Les nouvelles limites sont inférieures à l'utilisation actuelle. 
                  L'entreprise ne pourra plus créer de nouveaux éléments.
                </div>
              )
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || fetching}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Main Component
export default function SuperAdminDashboardPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [alerts, setAlerts] = useState({ alerts: [], counts: {} });
  const [activeSection, _setActiveSection] = useState(() => searchParams.get('tab') || 'overview');

  const setActiveSection = useCallback((next) => {
    _setActiveSection(next);
    setSearchParams(next && next !== 'overview' ? { tab: next } : {}, { replace: true });
  }, [setSearchParams]);

  // Sync state when URL changes externally (sidebar click, back/forward)
  useEffect(() => {
    const tab = searchParams.get('tab') || 'overview';
    if (tab !== activeSection) _setActiveSection(tab);
  }, [searchParams]);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  
  // Dialogs
  const [actionDialog, setActionDialog] = useState({ open: false, company: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, company: null });
  const [limitsDialog, setLimitsDialog] = useState({ open: false, company: null });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Use allSettled so a single failing endpoint doesn't blank the whole dashboard
      const [kpisRes, companiesRes, alertsRes] = await Promise.allSettled([
        api.get('/superadmin/dashboard/kpis'),
        api.get('/superadmin/companies'),
        api.get('/superadmin/alerts'),
      ]);

      if (kpisRes.status === 'fulfilled') setKpis(kpisRes.value.data);
      else console.warn('KPIs unavailable:', kpisRes.reason?.message);

      if (companiesRes.status === 'fulfilled') {
        setCompanies(companiesRes.value.data?.companies || []);
      } else {
        console.warn('Companies unavailable:', companiesRes.reason?.message);
      }

      if (alertsRes.status === 'fulfilled') setAlerts(alertsRes.value.data);
      else console.warn('Alerts unavailable:', alertsRes.reason?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter companies
  const filteredCompanies = companies.filter(company => {
    const matchesSearch = !searchTerm || 
      company.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || company.status === statusFilter;
    const matchesPlan = planFilter === 'all' || company.subscription?.plan === planFilter;
    return matchesSearch && matchesStatus && matchesPlan;
  });

  // Handle company action
  const handleCompanyAction = async (companyId, action, reason, duration, upgradeOptions = {}) => {
    try {
      const payload = { action, reason };
      if (duration && ['grant_trial', 'grant_grace_period', 'activate_demo', 'extend_demo'].includes(action)) {
        payload.duration_days = duration;
      }
      // Options spécifiques pour l'upgrade PRO
      if (action === 'upgrade' && upgradeOptions) {
        payload.duration_value = upgradeOptions.durationValue || 1;
        payload.duration_unit = upgradeOptions.durationUnit || 'month';
        payload.user_count = upgradeOptions.userCount || 10;
        payload.max_users = upgradeOptions.maxUsers || upgradeOptions.userCount || 10;
        payload.max_employees = upgradeOptions.maxEmployees || (upgradeOptions.userCount || 10) * 50;
      }
      // Plan accordé pendant la démo
      if (action === 'activate_demo' && upgradeOptions?.demoPlan) {
        payload.demo_plan = upgradeOptions.demoPlan;
      }
      const response = await api.post(`/superadmin/companies/${companyId}/action`, payload);
      toast.success(response.data.message || 'Action effectuée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'action');
    }
  };

  // Handle company deletion
  const handleDeleteCompany = async (companyId) => {
    try {
      const response = await api.delete(`/superadmin/companies/${companyId}`);
      toast.success(response.data.message || 'Entreprise supprimée avec succès');
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la suppression';
      toast.error(message);
      throw error;
    }
  };

  // Get real alerts (only those with actual issues)
  const realAlerts = (alerts.alerts || []).filter(alert => {
    // Filter out mock/test alerts
    if (!alert.companyId && !alert.type) return false;
    return true;
  });

  // Count active issues
  const activeIssues = (alerts.counts?.critical || 0) + (alerts.counts?.warning || 0);

  return (
    <DashboardLayout title="Cockpit SuperAdmin">
      <div className="min-h-[calc(100vh-120px)]">
        {/* Horizontal tabs kept for mobile only (sidebar handles desktop nav) */}
        <div className="mb-6 -mx-4 sm:-mx-6 lg:-mx-8 border-b border-ink-200 bg-white/60 backdrop-blur-sm sticky top-16 z-20 lg:hidden">
          <div className="overflow-x-auto px-4 sm:px-6 lg:px-8">
            <div className="flex gap-1 py-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveSection(item.id)}
                    data-testid={`superadmin-tab-${item.id}`}
                    className={`relative flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? 'text-brand bg-brand-soft'
                        : 'text-ink-600 hover:text-ink-900 hover:bg-ink-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                    {item.id === 'overview' && activeIssues > 0 && (
                      <Badge className="bg-red-100 text-red-700 text-xs">
                        {activeIssues}
                      </Badge>
                    )}
                    {isActive && <span className="absolute inset-x-2 -bottom-1 h-0.5 bg-brand rounded-full" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="w-full">
          {/* Overview Section */}
          {activeSection === 'overview' && (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-xl font-bold text-slate-900">Tableau de bord</h1>
                  <p className="text-sm text-slate-500">Vue d'ensemble de la plateforme</p>
                </div>
                <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Actualiser
                </Button>
              </div>

              {/* KPIs - Simplified */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Entreprises</p>
                        <p className="text-2xl font-bold">{loading ? '—' : kpis?.companies?.total || 0}</p>
                      </div>
                      <Building2 className="w-8 h-8 text-blue-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Comptes PRO</p>
                        <p className="text-2xl font-bold">{loading ? '—' : kpis?.companies?.pro || 0}</p>
                      </div>
                      <Crown className="w-8 h-8 text-amber-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">MRR</p>
                        <p className="text-2xl font-bold">{loading ? '—' : formatCurrency(kpis?.financial?.mrr)}</p>
                      </div>
                      <DollarSign className="w-8 h-8 text-emerald-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Utilisateurs</p>
                        <p className="text-2xl font-bold">{loading ? '—' : kpis?.users?.total || 0}</p>
                      </div>
                      <Users className="w-8 h-8 text-purple-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-6">
                {/* Recent Companies */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Dernières inscriptions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <div className="space-y-3">
                        {[1,2,3].map(i => <Skeleton key={i} className="h-12" />)}
                      </div>
                    ) : companies.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-4">Aucune entreprise</p>
                    ) : (
                      <div className="space-y-3">
                        {companies.slice(0, 5).map((company) => (
                          <div key={company.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                                <Building2 className="w-4 h-4 text-slate-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium">{company.name}</p>
                                <p className="text-xs text-slate-500">{company.employeesCount || 0} employés</p>
                              </div>
                            </div>
                            <StatusBadge status={company.status} />
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Alerts - Only show if there are real alerts */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Bell className="w-4 h-4" />
                      Alertes
                      {activeIssues > 0 && (
                        <Badge className="bg-red-100 text-red-700">{activeIssues}</Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <div className="space-y-3">
                        {[1,2].map(i => <Skeleton key={i} className="h-12" />)}
                      </div>
                    ) : realAlerts.length === 0 ? (
                      <div className="text-center py-6">
                        <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">Aucune alerte active</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {realAlerts.slice(0, 4).map((alert, idx) => (
                          <div 
                            key={idx}
                            className={`p-3 rounded-lg border ${
                              alert.severity === 'critical' ? 'bg-red-50 border-red-200' :
                              alert.severity === 'warning' ? 'bg-amber-50 border-amber-200' :
                              'bg-blue-50 border-blue-200'
                            }`}
                          >
                            <div className="flex items-start gap-2">
                              {alert.severity === 'critical' ? 
                                <XCircle className="w-4 h-4 text-red-500 mt-0.5" /> :
                                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />
                              }
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-900">{alert.title}</p>
                                <p className="text-xs text-slate-600 truncate">{alert.message}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Companies Section */}
          {activeSection === 'companies' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-xl font-bold text-slate-900">Entreprises</h1>
                  <p className="text-sm text-slate-500">{companies.length} entreprises enregistrées</p>
                </div>
              </div>

              {/* Filters */}
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="active">Actif</SelectItem>
                    <SelectItem value="at_risk">À risque</SelectItem>
                    <SelectItem value="suspended">Suspendu</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={planFilter} onValueChange={setPlanFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="free">Gratuit</SelectItem>
                    <SelectItem value="pro">BUSINESS</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Companies List */}
              <Card>
                <CardContent className="p-0">
                  {loading ? (
                    <div className="p-4 space-y-3">
                      {[1,2,3,4].map(i => <Skeleton key={i} className="h-16" />)}
                    </div>
                  ) : filteredCompanies.length === 0 ? (
                    <div className="text-center py-12">
                      <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">Aucune entreprise trouvée</p>
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="text-left p-4 text-sm font-medium text-slate-600">Entreprise</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-600">Plan</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-600">Employés</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-600">Statut</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-600">Inscription</th>
                          <th className="text-right p-4"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {filteredCompanies.map((company) => (
                          <tr key={company.id} className="hover:bg-slate-50">
                            <td className="p-4">
                              <div>
                                <p className="font-medium text-slate-900">{company.name}</p>
                                <p className="text-sm text-slate-500">{company.admin?.email}</p>
                              </div>
                            </td>
                            <td className="p-4">
                              <Badge className={company.subscription?.plan === 'pro' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}>
                                {company.subscription?.plan === 'pro' ? 'BUSINESS' : 'STARTER'}
                              </Badge>
                              {company.subscription?.isDemo && (
                                <div className="mt-1 text-[10px] text-purple-700 font-medium">
                                  Démo
                                  {company.subscription?.demoExpiresAt && (
                                    <> · expire le {new Date(company.subscription.demoExpiresAt).toLocaleDateString('fr-FR')}</>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="p-4 text-slate-600">{company.employeesCount || 0}</td>
                            <td className="p-4"><StatusBadge status={company.status} /></td>
                            <td className="p-4 text-sm text-slate-500">{formatDate(company.createdAt)}</td>
                            <td className="p-4 text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => setActionDialog({ open: true, company })}>
                                    <Settings className="w-4 h-4 mr-2" />
                                    Gérer
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => setLimitsDialog({ open: true, company })}>
                                    <Users className="w-4 h-4 mr-2" />
                                    Modifier les limites
                                  </DropdownMenuItem>
                                  <DropdownMenuItem>
                                    <Eye className="w-4 h-4 mr-2" />
                                    Voir détails
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={() => setDeleteDialog({ open: true, company })}
                                    className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Supprimer
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Other Sections - Use existing components */}
          {activeSection === 'lean-insights' && <SuperAdminLeanInsights />}
          {activeSection === 'growth' && <SuperAdminGrowthInsights />}
          {activeSection === 'billing' && <SuperAdminBillingDashboard />}
          {activeSection === 'users' && <SuperAdminUsersDashboard />}
          {activeSection === 'ai-credits' && <AICreditsManager />}
          {activeSection === 'ai' && <SuperAdminAIDashboard />}
          {activeSection === 'modules' && <SuperAdminModulesDashboard />}
          {activeSection === 'settings' && <SuperAdminSettingsDashboard />}
          {activeSection === 'admins' && <SuperAdminAdminsDashboard />}
          {activeSection === 'expirations' && <SuperAdminExpirationsDashboard />}
          {activeSection === 'backup' && <SuperAdminBackupDashboard />}
        </main>
      </div>

      {/* Action Dialog */}
      <CompanyActionDialog
        isOpen={actionDialog.open}
        onClose={() => setActionDialog({ open: false, company: null })}
        company={actionDialog.company}
        onAction={handleCompanyAction}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteCompanyDialog
        isOpen={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, company: null })}
        company={deleteDialog.company}
        onDelete={handleDeleteCompany}
      />

      {/* Limits Dialog */}
      <CompanyLimitsDialog
        isOpen={limitsDialog.open}
        onClose={() => setLimitsDialog({ open: false, company: null })}
        company={limitsDialog.company}
        onSuccess={fetchData}
      />
    </DashboardLayout>
  );
}
